import { useEffect, useRef, useState } from "react";
import { useAppContext } from "../AppContext";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";

import styles from "./SpwkQuestionnaireDialog.module.css";
import { Clear } from "@mui/icons-material";
import { useUniFuncList } from "../../../uni-questionnaire/dao/UniFuncDAO";
import { Toast } from "../Toast";

const SpwkTypes = ["新進", "定期", "變更", "追蹤"];

class SpecialWork {
  code = "";
  type = "";
  title = "";

  /**
   *
   */
  constructor(code: string, title: string) {
    this.code = code;
    this.type = "定期";
    this.title = title;
  }
}

const SpwkQuestionnaireDialog = ({
  onDialogClosed,
}: {
  onDialogClosed: (selectedList: SpecialWork[]) => void;
}) => {
  /**
   * 關閉對話框處理
   */
  const handleClose = () => {
    onDialogClosed([]);
    setOpen(false);
  };

  /**
   * 加入特作問卷
   * @param spwk 特作代碼
   * @returns
   */
  const addSpwkCode = (spwk: string) => {
    const func = _UniFuncList.find((x) => x.func_code === spwk);
    if (!spwk || func == null || !/^(A01|A24|A98|B\d{2})$/.test(spwk)) {
      if (inputRef.current) {
        inputRef.current.select();
      }
      Toast.ShowWarning(ctx, "請輸入正確的特作代碼");
      return;
    }

    if (spwkList.some((x) => x.code === spwk)) {
      if (inputRef.current) {
        inputRef.current.select();
      }
      Toast.ShowWarning(ctx, "特作代碼已存在列表中");
      return;
    }

    const data = new SpecialWork(func.func_code, func.func_title);

    setSpwkList([...spwkList, data]);
    setSpwkCode("");
  };

  /**
   * 移除指定特作問卷
   * @param spwk 特作代碼
   * @returns
   */
  const removeSpwkCode = (spwk: string) => {
    if (!spwkList.some((x) => x.code === spwk)) {
      return;
    }
    setSpwkList(spwkList.filter((x) => x.code !== spwk));
  };

  /**
   * 更新特作問卷的檢查時期
   * @param i 問卷資料索引值
   * @param newType 檢查時期
   */
  const updateSpwkType = (i: number, newType: string) => {
    const updatedList = [...spwkList];
    updatedList[i].type = newType;
    setSpwkList(updatedList);
  };

  /**
   * 確認選取事件處理
   */
  const handleConfirm = () => {
    // pass special work list to parent
    onDialogClosed(spwkList);
    setOpen(false);
  };

  const [open, setOpen] = useState(true);
  const [spwkCode, setSpwkCode] = useState("");
  const [spwkList, setSpwkList] = useState<SpecialWork[]>([]);
  const { _UniFuncList } = useUniFuncList();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const ctx = useAppContext();

  useEffect(() => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  }, []);

  return (
    <Dialog maxWidth="md" fullWidth={true} open={open} onClose={handleClose}>
      <DialogTitle>
        設定特作問卷
        <FormGroup className={styles.formGroup} row={true}>
          <TextField
            inputRef={inputRef}
            size="small"
            label="特作代碼"
            value={spwkCode}
            onChange={(e) => setSpwkCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                addSpwkCode(spwkCode);
              }
            }}
          />
          <Button
            onClick={() => {
              addSpwkCode(spwkCode);
            }}
          >
            加入
          </Button>
        </FormGroup>
      </DialogTitle>
      <DialogContent dividers={true}>
        {!_UniFuncList && <div>Loading...</div>}
        {_UniFuncList && (
          <div className={styles.body}>
            <Typography variant="h6">特作列表</Typography>
            {spwkList.map((spwk, i) => {
              return (
                <div className={styles.container} key={i}>
                  <IconButton
                    color="error"
                    onClick={() => removeSpwkCode(spwk.code)}
                  >
                    <Clear />
                  </IconButton>
                  <div className={styles.content}>
                    <div className={styles.title}>
                      <Typography variant="subtitle2">{spwk.code}</Typography>
                      <Typography variant="subtitle2">{spwk.title}</Typography>
                    </div>
                    {spwk.code !== "A98" && (
                      <RadioGroup
                        row={true}
                        value={spwk.type}
                        onChange={(e) => {
                          updateSpwkType(i, e.target.value);
                        }}
                      >
                        {SpwkTypes.filter((type) => {
                          if (spwk.code === "A98") {
                            return false;
                          } else if (
                            spwk.code === "A01" &&
                            type !== "新進" &&
                            type !== "定期"
                          ) {
                            return false;
                          }
                          return true;
                        }).map((type, i) => (
                          <FormControlLabel
                            key={i}
                            value={type}
                            label={type}
                            control={<Radio />}
                          />
                        ))}
                      </RadioGroup>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
      <DialogActions className={styles.actions}>
        <Button onClick={handleClose} color="inherit">
          取消
        </Button>
        <Button variant="contained" onClick={handleConfirm}>
          確認
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpwkQuestionnaireDialog;
